import { type ComponentProps, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { TextField } from '../forms-fields';
import { TermAndPolicy } from '../term-and-policy';
import { useTranslation } from 'next-i18next';
import { Button, getColorVar, Text, withDialog } from '@kidslabs/mario-nextjs-react-ui-core';
import type { RegisterFormInput, RegisterFormProps } from './register-form.types';
import { useRegisterSchema } from './register-form.schema';
import { useAuthProvider } from '../../providers';
import { ErrorCodesEnum } from '../../enums';
import { clearLoginSessionData, authStore$ } from '../../stores/auth.store';
import { useAuth } from '../../hooks';
import { type CustomerReferralCodeSignature, useLookupReferralCodeLazyQuery } from '../../types/front.graphql';
import type { UseFormReturn } from 'react-hook-form';
import styles from './register-form.module.css';

/**
 * Helper component that persists form data to the store on unmount.
 * Defined at module level to avoid remounting on parent re-renders.
 */
const RegisterFormDataSync: FC<{
    getValues: UseFormReturn<RegisterFormInput>['getValues'];
    isAgreeTermAndPolicy: boolean;
}> = ({ getValues, isAgreeTermAndPolicy }) => {
    const getValuesRef = useRef(getValues);
    const isAgreeRef = useRef(isAgreeTermAndPolicy);

    useEffect(() => {
        getValuesRef.current = getValues;
    }, [getValues]);

    useEffect(() => {
        isAgreeRef.current = isAgreeTermAndPolicy;
    }, [isAgreeTermAndPolicy]);

    useEffect(() => {
        return () => {
            const values = getValuesRef.current();
            authStore$.pendingRegistrationInput.set({
                name: values.name,
                email: values.email,
                referralCode: values.referral_code,
                isAgreeTermAndPolicy: isAgreeRef.current,
            });
        };
    }, []);

    return null;
};

const RegisterFormContent: FC<RegisterFormProps> = ({ FormComponent, hideDialog, setDialogOptions, showDialog }) => {
    const { t } = useTranslation('auth');
    const { registerNewAccountByOtp } = useAuthProvider();
    const { setCurrentStep } = useAuth();
    const registerSchema = useRegisterSchema();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [referralError, setReferralError] = useState<string>('');
    const [fetchLookupReferralCodeQuery] = useLookupReferralCodeLazyQuery();

    // Restore checkbox state from store (preserved across step transitions)
    const pendingInput = authStore$.pendingRegistrationInput.get();
    const [isAgreeTermAndPolicy, setIsAgreeTermAndPolicy] = useState(() => pendingInput?.isAgreeTermAndPolicy ?? false);

    // Restore form default values from store
    const formDefaultValues = useMemo(
        () => ({
            name: pendingInput?.name ?? '',
            email: pendingInput?.email ?? '',
            referral_code: pendingInput?.referralCode ?? '',
        }),
        // Only compute once on mount — pendingInput is read from store at mount time
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const loadingStyle: ComponentProps<typeof Button>['loadingProps'] = useMemo(() => {
        return {
            color: getColorVar('primary', '0'),
        };
    }, []);

    const handleShowDialog = useCallback(
        (message: string) => {
            setDialogOptions({
                title: t('text_notification', 'Thông báo'),
                content: message,
                acceptLabel: t('text_agree', 'Đồng ý'),
                loading: false,
                onAccept: () => hideDialog(),
            });
            showDialog();
        },
        [hideDialog, setDialogOptions, showDialog, t]
    );

    const handleSuccess = useCallback(() => {
        clearLoginSessionData();
        setIsSubmitting(false);
        // Transition to success celebration view instead of closing immediately.
        // The AuthSuccessView will call onAuthSuccess after the animation completes.
        setCurrentStep('AuthSuccess');
    }, [setCurrentStep]);

    const verifyReferralCode = useCallback(
        async (referralCode: string) => {
            if (!referralCode) {
                return { validatedReferralCode: undefined, isError: false };
            }

            const result = await fetchLookupReferralCodeQuery({
                variables: { code: referralCode },
            });

            if (!result?.data?.lookupReferralCode) {
                setReferralError(t('error_referral_code_invalid'));
                return { validatedReferralCode: undefined, isError: true };
            }

            const { salt, sign } = result?.data?.lookupReferralCode as CustomerReferralCodeSignature;
            if (!salt || !sign) {
                setReferralError(t('error_referral_code_not_found'));
                return { validatedReferralCode: undefined, isError: true };
            }

            return {
                validatedReferralCode: { code: referralCode, salt, sign },
                isError: false,
            };
        },
        [fetchLookupReferralCodeQuery, t]
    );

    const handleAgree = useCallback((value: boolean) => {
        setIsAgreeTermAndPolicy(value);
    }, []);

    const onSubmit = useCallback(
        async (data: RegisterFormInput) => {
            try {
                setIsSubmitting(true);
                setReferralError('');

                // Verify referral code if provided
                const referralResult = await verifyReferralCode(data?.referral_code ?? '');
                if (referralResult?.isError) {
                    setIsSubmitting(false);
                    return;
                }

                // Store referral code in authStore before register call
                if (referralResult?.validatedReferralCode) {
                    const currentData = authStore$.initValidatePhoneData.get();
                    authStore$.initValidatePhoneData.set({
                        ...currentData,
                        phoneNumber: currentData?.phoneNumber ?? '',
                        referralCode: referralResult.validatedReferralCode,
                    });
                }

                const nameToSplit = data.name.trim();
                const [firstName = '', ...lastNameParts] = nameToSplit.split(/\s+/);
                const lastName = lastNameParts.join(' ');

                const result = await registerNewAccountByOtp({
                    email: data?.email,
                    first_name: firstName,
                    last_name: lastName,
                    password: undefined,
                    website_id: 1,
                });

                if (result.errorCode === ErrorCodesEnum.SUCCESS) {
                    handleSuccess();
                } else if (result.errorCode === ErrorCodesEnum.OTP_VERIFICATION_REQUIRED) {
                    setIsSubmitting(false);
                    setCurrentStep('SelectChannel');
                } else {
                    handleShowDialog(result.message);
                }
            } catch (error) {
                const message = error instanceof Error ? error.message : t('text_unknown_error', 'Đã có lỗi xảy ra');
                handleShowDialog(message);
            } finally {
                setIsSubmitting(false);
            }
        },
        [handleShowDialog, registerNewAccountByOtp, handleSuccess, setCurrentStep, verifyReferralCode, t]
    );

    return (
        <FormComponent<RegisterFormInput>
            onSubmit={onSubmit}
            validationSchema={registerSchema}
            className={styles.formContainer}
            useFormProps={{ defaultValues: formDefaultValues }}
        >
            {({ control, formState: { errors }, resetField, getValues }) => (
                <div className={styles.container}>
                    <RegisterFormDataSync getValues={getValues} isAgreeTermAndPolicy={isAgreeTermAndPolicy} />

                    <div className={styles.formFieldWrapper}>
                        <div className={styles.labelWrapper}>
                            <Text className={styles.label}>{t('text_name')}</Text>
                        </div>
                        <TextField
                            name="name"
                            control={control}
                            errorMessage={errors?.name?.message}
                            placeHolder={t('text_latin_characters_only')}
                        />
                    </div>

                    <div className={styles.formFieldWrapper}>
                        <div className={styles.labelWrapper}>
                            <Text className={styles.label}>{t('Email')}</Text>
                        </div>
                        <TextField
                            name="email"
                            control={control}
                            errorMessage={errors?.email?.message}
                            placeHolder={t('example@gmail.com')}
                        />
                    </div>

                    <TextField
                        control={control}
                        name="referral_code"
                        placeHolder={t('placeholder_referral_code_field')}
                        onClearValue={() => {
                            resetField('referral_code');
                            setReferralError('');
                        }}
                        errorMessage={errors?.referral_code?.message || referralError}
                    />

                    <TermAndPolicy checked={isAgreeTermAndPolicy} onCheckedChange={handleAgree} />

                    <div className={styles.footer}>
                        <Button
                            type="submit"
                            aria-label="Save registration info"
                            disabled={isSubmitting || !isAgreeTermAndPolicy}
                            loading={isSubmitting}
                            className={styles.submitButton}
                            loadingProps={loadingStyle}
                        >
                            <Text className={styles.textButton}>{t('text_save', 'Lưu')}</Text>
                        </Button>
                    </div>
                </div>
            )}
        </FormComponent>
    );
};

const RegisterFormInner: FC<RegisterFormProps> = props => {
    return <RegisterFormContent {...props} />;
};

export const RegisterForm = withDialog(RegisterFormInner);
